import { useRef, useState } from 'react';
import { getSession } from '~/lib/auth';
import { isImageFile, prepareImage } from '~/lib/imagePrep';

/**
 * Universal image input. Accepts file uploads (drag-drop or picker) and
 * routes them to /api/admin/media/upload, which writes to R2 and returns
 * a public URL. Also accepts pasted URLs for external images (Unsplash,
 * existing CDN, etc.).
 *
 * Used by the generated section + list forms (formgen), plus image columns on
 * marketing_services / drip_menu / etc. — anywhere an editor needs to
 * point at an image.
 */

interface ImagePickerProps {
  value: string;
  onChange: (next: string) => void;
  /**
   * Aspect ratio of the preview thumbnail. **Always pass the same ratio
   * the image will render at on the public site** (e.g. '4/5' for hero
   * portraits, '4/3' for service cards, '16/10' for drip cards).
   * Channa needs to see what she'll get — wrong-ratio previews cause
   * upload-then-cropped surprises.
   *
   * Accepts any CSS aspect-ratio string ('w/h' or single number).
   */
  aspect?: string;
  /** Label shown above the picker (optional). */
  label?: string;
  /** Render as a smaller compact variant (for inline use in card rows). */
  compact?: boolean;
}

const API_BASE = import.meta.env.VITE_API_URL ?? 'https://api.valleyvitalitywellness.com';

export function ImagePicker({ value, onChange, aspect = '1/1', label, compact }: ImagePickerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);

  async function uploadFile(file: File) {
    setUploading(true);
    setError(null);
    try {
      const session = getSession();
      if (!session) throw new Error('Not signed in');
      // Shrink + re-encode in the browser first: phone photos are huge and
      // the API refuses anything over 10 MB.
      const prepared = await prepareImage(file);
      const form = new FormData();
      form.append('file', prepared);
      const res = await fetch(`${API_BASE}/api/admin/media/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: form,
      });
      // The API answers JSON, but a proxy/network failure may not.
      const data = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
      if (!res.ok || !data.url) {
        throw new Error(data.error || `Upload failed (${res.status})`);
      }
      onChange(data.url);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploading(false);
    }
  }

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const file = files[0];
    // isImageFile also recognises .heic/.heif, which Windows reports with no type.
    if (!file || !isImageFile(file)) {
      setError('Only image files are supported.');
      return;
    }
    void uploadFile(file);
  }

  // Fixed thumbnail WIDTH — height derives from `aspect` so the preview
  // mirrors the target rendering ratio. A 4/5 hero shows tall, a 16/10
  // drip card shows landscape, a 1/1 about-personal shows square.
  const thumbWidth = compact ? 48 : 96;

  return (
    <div>
      {label && (
        <label
          style={{
            display: 'block',
            fontSize: 11.5,
            fontWeight: 600,
            letterSpacing: '0.04em',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          {label}
        </label>
      )}

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        style={{
          display: 'grid',
          gridTemplateColumns: `${thumbWidth}px 1fr`,
          gap: 10,
          alignItems: 'flex-start',
          padding: 8,
          border: `1px ${dragOver ? 'dashed' : 'solid'} ${dragOver ? 'var(--teal)' : 'var(--line)'}`,
          borderRadius: 8,
          background: dragOver ? 'rgba(95, 168, 162, 0.06)' : 'var(--card)',
          transition: 'border-color 120ms, background 120ms',
        }}
      >
        {/* Thumbnail — width is fixed by the grid column; height comes
            from aspect-ratio so the preview shape matches the target. */}
        {value ? (
          <img
            src={value}
            alt=""
            style={{
              width: '100%',
              aspectRatio: aspect,
              objectFit: 'cover',
              borderRadius: 6,
              border: '1px solid var(--line-soft)',
              background: 'var(--mint)',
              display: 'block',
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              aspectRatio: aspect,
              borderRadius: 6,
              border: '1px dashed var(--line)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-muted)',
              fontSize: 10,
              textAlign: 'center',
              padding: 4,
              lineHeight: 1.2,
            }}
          >
            {aspect}
          </div>
        )}

        {/* Controls */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              style={{
                background: 'var(--teal)',
                color: '#fff',
                border: 'none',
                padding: '5px 12px',
                borderRadius: 6,
                fontSize: 12.5,
                fontWeight: 600,
                cursor: uploading ? 'wait' : 'pointer',
              }}
            >
              {uploading ? 'Uploading…' : value ? 'Replace' : 'Upload image'}
            </button>
            <button
              type="button"
              onClick={() => setShowUrlInput((v) => !v)}
              style={{
                background: 'transparent',
                color: 'var(--text-muted)',
                border: '1px solid var(--line)',
                padding: '5px 10px',
                borderRadius: 6,
                fontSize: 12.5,
                cursor: 'pointer',
              }}
            >
              {showUrlInput ? 'Hide URL' : 'Use URL'}
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange('')}
                style={{
                  background: 'transparent',
                  color: '#c44545',
                  border: '1px solid rgba(196, 69, 69, 0.4)',
                  padding: '5px 10px',
                  borderRadius: 6,
                  fontSize: 12.5,
                  cursor: 'pointer',
                }}
              >
                Clear
              </button>
            )}
          </div>

          {!showUrlInput && value && (
            <div
              style={{
                fontSize: 11,
                color: 'var(--text-muted)',
                fontFamily: "'SF Mono', Menlo, Consolas, monospace",
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
              title={value}
            >
              {value}
            </div>
          )}

          {showUrlInput && (
            <input
              type="text"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="https://… (paste an image URL)"
              style={{
                fontFamily: "'SF Mono', Menlo, Consolas, monospace",
                fontSize: 12,
                padding: '6px 9px',
                border: '1px solid var(--line)',
                borderRadius: 6,
                background: 'var(--card)',
                color: 'var(--text)',
                width: '100%',
              }}
            />
          )}

          {error && (
            <div role="alert" style={{ fontSize: 12.5, lineHeight: 1.4, color: '#b83b3b' }}>
              {error}
            </div>
          )}

          {!compact && !value && !uploading && !error && (
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.4 }}>
              Drag a photo here, click Upload, or paste a URL. Big phone photos are shrunk for you.
            </div>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          handleFiles(e.target.files);
          // Reset so picking the same file again (after an error) still fires.
          e.target.value = '';
        }}
      />
    </div>
  );
}
